// Avatar look + cosmetics catalog, shared by client and server.
// Art style: Y2K streetwear pixel chibis. Each item maps to a sprite layer in
// src/client/chibi.ts through its `kind` and colors.

export type Slot = "top" | "bottom" | "shoes" | "hat" | "glasses" | "acc" | "aura";

export interface Item {
  id: string;
  slot: Slot;
  name: string;
  price: number; // in Bits, the fake soft currency; 0 = everyone owns it
  kind: string; // sprite shape, e.g. "hoodie", "baggy", "catbeanie"
  color: string;
  accent?: string; // trim / print / lens color
  accent2?: string;
}

const it = (id: string, slot: Slot, name: string, price: number, kind: string, color: string, accent?: string, accent2?: string): Item =>
  ({ id, slot, name, price, kind, color, accent, accent2 });

export const ITEMS: Item[] = [
  // Tops
  it("top_tee_white", "top", "White Tee", 0, "tee", "#f4f4f6"),
  it("top_tee_black", "top", "Black Tee", 60, "tee", "#26262e"),
  it("top_hoodie_grey", "top", "Grey Hoodie", 120, "hoodie", "#8c8c98", "#f0f0f4"),
  it("top_heart_tee", "top", "Heart Tee", 80, "tee", "#f4f4f6", undefined, "#e23b5a"),
  it("top_tank_white", "top", "White Tank", 60, "tank", "#f4f4f6"),
  it("top_crop_black", "top", "Heart Crop Top", 120, "crop", "#1e1e26", undefined, "#e23b5a"),
  it("top_hoodie_black", "top", "Black Hoodie", 150, "hoodie", "#2b2b35", "#e8e8f0"),
  it("top_hoodie_pink", "top", "Pink Hoodie", 180, "hoodie", "#f39cbc", "#ffffff"),
  it("top_hoodie_blue", "top", "Blue Heart Hoodie", 220, "hoodie", "#1e1e26", "#5ab0ff", "#5ab0ff"),
  it("top_track_navy", "top", "Track Jacket", 300, "track", "#1f3a6a", "#f0f0f0"),
  it("top_puffer_red", "top", "Red Puffer", 400, "puffer", "#c8302f"),
  it("top_puffer_cream", "top", "Cream Puffer", 400, "puffer", "#e8dcc6"),
  it("top_varsity", "top", "Varsity Jacket", 450, "varsity", "#2a4a2e", "#f0ead8", "#f0ead8"),
  it("top_jacket_maroon", "top", "Leather Jacket", 500, "jacket", "#5a1f2a", "#e8e8e8"),
  it("top_star_tee", "top", "Chrome Star Tee", 120, "startee", "#1e1e24", undefined, "#d8dce6"),
  it("top_skull_tee", "top", "Skull Tee", 140, "skulltee", "#f4f4f6", undefined, "#1e1e24"),
  it("top_wind_lime", "top", "Acid Windbreaker", 380, "windbreaker", "#1e1e24", "#c6ff00", "#c6ff00"),
  it("top_wind_purple", "top", "Teal Windbreaker", 380, "windbreaker", "#4a2f8a", "#2ad6c0", "#f0f0f4"),
  it("top_jersey_black", "top", "Black Jersey", 320, "jersey", "#1e1e24", "#c6ff00", "#c6ff00"),
  it("top_jersey_white", "top", "Home Jersey", 320, "jersey", "#f2f2f4", "#d0283a", "#d0283a"),
  it("top_flannel_red", "top", "Red Flannel", 350, "flannel", "#a8262e", "#1e1e24", "#1e1e24"),
  it("top_trench_black", "top", "Leather Trench", 600, "trench", "#1c1c22", "#c8ccd6", "#2a2a32"),
  it("top_trench_oxblood", "top", "Oxblood Trench", 600, "trench", "#4e1820", "#c8ccd6", "#1c1c22"),
  it("top_shredknit", "top", "Shredded Knit", 320, "shredknit", "#3a3a42"),
  it("top_shredknit_stripe", "top", "Striped Shred Knit", 340, "shredknit", "#1e1e24", "#b02232"),
  it("top_mesh_black", "top", "Mesh Long Sleeve", 240, "mesh", "#1a1a20", undefined, "#b02232"),
  it("top_bat_tee", "top", "Bat Tee", 140, "battee", "#1e1e24", undefined, "#c0283a"),
  it("top_blazer_black", "top", "Oversized Blazer", 450, "blazer", "#26262c", "#d8b860", "#f4f4f6"),
  it("top_blazer_brown", "top", "Chocolate Blazer", 450, "blazer", "#5a3a2c", "#d8b860", "#f2ede4"),
  it("top_blazer_grey", "top", "Grey Blazer", 450, "blazer", "#8a8a94", "#e8e8ee", "#1e1e24"),
  it("top_babytee_white", "top", "Baby Tee", 120, "babytee", "#f4f4f6", "#1e1e24"),
  it("top_babytee_blue", "top", "Baby Blue Tee", 120, "babytee", "#bcd6ee", "#f4f4f6"),
  it("top_moto_black", "top", "Cropped Leather Jacket", 480, "moto", "#1e1e24", "#d8dce6"),
  it("top_crewneck_grey", "top", "Grey Crewneck", 180, "crewneck", "#a2a2ac"),
  it("top_butterfly_pink", "top", "Butterfly Top", 260, "butterfly", "#f07ab4", "#fff0f8"),
  it("top_butterfly_silver", "top", "Silver Butterfly Top", 280, "butterfly", "#c4c8d6", "#ffffff"),
  it("top_tube_pink", "top", "Pink Tube Top", 140, "tube", "#f48cc0"),
  it("top_tube_white", "top", "White Tube Top", 140, "tube", "#f2f2f6"),
  it("top_tube_blue", "top", "Baby Blue Tube Top", 140, "tube", "#a6d0f2"),
  it("top_velour_pink", "top", "Pink Velour Hoodie", 380, "velour", "#e45e9e", "#f4f4f8"),
  it("top_velour_blue", "top", "Baby Blue Velour Hoodie", 380, "velour", "#86bce8", "#f4f4f8"),
  it("top_jeanjacket", "top", "Cropped Denim Jacket", 400, "jeanjacket", "#6a8cba", "#d8a04a"),
  it("top_babytee_pink", "top", "Pink Baby Tee", 140, "babytee", "#f6bcd6", "#f4f4f6", "#e23b8a"),
  // Bottoms
  it("bottom_baggy_blue", "bottom", "Baggy Jeans", 100, "baggy", "#4a6fa8"),
  it("bottom_baggy_black", "bottom", "Black Jeans", 100, "baggy", "#2a2a30"),
  it("bottom_shorts_denim", "bottom", "Denim Shorts", 0, "shorts", "#7d9cc0"),
  it("bottom_skirt_black", "bottom", "Pleated Skirt", 80, "skirt", "#2b2b35"),
  it("bottom_baggy_light", "bottom", "Light Wash Jeans", 120, "baggy", "#8fb3d9"),
  it("bottom_track_black", "bottom", "Track Pants", 180, "trackpants", "#24242c", "#f0f0f0"),
  it("bottom_cargo_black", "bottom", "Black Cargos", 200, "cargo", "#2a2a2e"),
  it("bottom_cargo_khaki", "bottom", "Khaki Cargos", 200, "cargo", "#c8b48a"),
  it("bottom_skirt_plaid", "bottom", "Plaid Skirt", 220, "skirt", "#b8303a", "#2a1a22"),
  it("bottom_plaid_blue", "bottom", "Plaid Pants", 250, "plaid", "#2e5aa8", "#8fc0ff"),
  it("bottom_jorts", "bottom", "Jorts", 150, "jorts", "#4a6fa8"),
  it("bottom_jorts_black", "bottom", "Black Jorts", 150, "jorts", "#2a2a30"),
  it("bottom_parachute", "bottom", "Parachute Pants", 280, "parachute", "#8a8e98", "#1e1e24", "#c6ff00"),
  it("bottom_parachute_black", "bottom", "Black Parachute Pants", 280, "parachute", "#24242a", "#c6ff00", "#c6ff00"),
  it("bottom_camo", "bottom", "Camo Cargos", 300, "camo", "#5d6b4a", "#3a4430", "#8c8a64"),
  it("bottom_camo_snow", "bottom", "Snow Camo Cargos", 320, "camo", "#d4d6dc", "#8a8c98", "#4a4c58"),
  it("bottom_flares_leather", "bottom", "Leather Flares", 380, "flares", "#1c1c22"),
  it("bottom_flares_ripped", "bottom", "Ripped Black Flares", 260, "flares", "#34343e", "#e8e8ec"),
  it("bottom_straps", "bottom", "Strap Pants", 340, "straps", "#1e1e24", "#8a8e98", "#e0e2ea"),
  it("bottom_wideleg_vintage", "bottom", "Vintage Wide-Legs", 220, "wideleg", "#6a8cba"),
  it("bottom_wideleg_black", "bottom", "Washed Black Wide-Legs", 220, "wideleg", "#3a3a44"),
  it("bottom_wideleg_cream", "bottom", "Cream Wide-Legs", 240, "wideleg", "#e6dcc8"),
  it("bottom_trousers_grey", "bottom", "Grey Trousers", 260, "trousers", "#7e7e88"),
  it("bottom_mini_leather", "bottom", "Leather Mini", 220, "mini", "#1e1e24", "#c8ccd6"),
  it("bottom_lowrise_light", "bottom", "Low-Rise Flares", 260, "lowrise", "#8fb3d9", "#f4f4f6", "#c8ccd6"),
  it("bottom_lowrise_dark", "bottom", "Dark Low-Rise Flares", 260, "lowrise", "#3e5a8e", "#e8e0d0", "#d8a04a"),
  it("bottom_lowrise_white", "bottom", "White Low-Rise Flares", 280, "lowrise", "#eceaf0", "#f07ab4", "#c8ccd6"),
  it("bottom_velour_pink", "bottom", "Pink Velour Pants", 320, "velourpants", "#e45e9e"),
  it("bottom_velour_blue", "bottom", "Baby Blue Velour Pants", 320, "velourpants", "#86bce8"),
  it("bottom_denimmini", "bottom", "Denim Mini", 200, "denimmini", "#6a8cba", "#e8e0d0"),
  // Shoes
  it("shoes_white", "shoes", "White Sneakers", 0, "sneakers", "#f4f4f8"),
  it("shoes_black", "shoes", "Black Sneakers", 100, "sneakers", "#1e1e24", "#ffffff", "#f0f0f0"),
  it("shoes_pink", "shoes", "Pink Kicks", 150, "sneakers", "#f4f4f8", "#ff4f9a"),
  it("shoes_boots", "shoes", "Combat Boots", 250, "boots", "#1e1e24"),
  it("shoes_tan", "shoes", "Tan Boots", 250, "boots", "#b07a4a"),
  it("shoes_hightop_black", "shoes", "Black High-Tops", 280, "hightops", "#1e1e24", "#c6ff00", "#f0f0f0"),
  it("shoes_hightop_red", "shoes", "Red High-Tops", 280, "hightops", "#c42a32", "#f4f4f8", "#f0f0f0"),
  it("shoes_slides", "shoes", "Slides and Socks", 160, "slides", "#1e1e24", "#1e1e24", "#e8e8ec"),
  it("shoes_platforms", "shoes", "Platform Boots", 420, "platforms", "#1c1c22", "#d8dce6", "#121216"),
  it("shoes_bulky", "shoes", "Bulky High-Tops", 340, "bulky", "#1e1e24", "#f0f0f4", "#d8d8e0"),
  it("shoes_flats_black", "shoes", "Ballet Flats", 180, "flats", "#1e1e24", "#4a4a56", "#121216"),
  it("shoes_flats_pink", "shoes", "Ballet Pink Flats", 180, "flats", "#efc6cc", "#d88a98", "#b88a90"),
  it("shoes_loafers_black", "shoes", "Chunky Loafers", 260, "loafers", "#1e1e24", "#d8b860", "#121216"),
  it("shoes_loafers_brown", "shoes", "Brown Loafers", 260, "loafers", "#5a3424", "#d8b860", "#24160e"),
  it("shoes_platsneaks", "shoes", "Platform Sneakers", 320, "platsneaks", "#f4f4f8", "#f07ab4", "#f4f4f8"),
  it("shoes_platsneaks_silver", "shoes", "Silver Platform Sneakers", 340, "platsneaks", "#c4c8d6", "#86bce8", "#f4f4f8"),
  it("shoes_flipflops", "shoes", "Platform Flip-Flops", 160, "flipflops", "#f48cc0", "#f4f4f8", "#c85a92"),
  // Hats
  it("hat_beanie_black", "hat", "Black Beanie", 120, "beanie", "#26262c"),
  it("hat_beanie_white", "hat", "White Beanie", 120, "beanie", "#e8e8ec"),
  it("hat_beanie_pom", "hat", "Pom Beanie", 150, "beanie", "#e05a7a", "#ffffff"),
  it("hat_backcap", "hat", "Backwards Cap", 150, "cap", "#1e1e24", "#ffffff"),
  it("hat_bucket", "hat", "Bucket Hat", 200, "bucket", "#f0ead8", "#2a4a2e"),
  it("hat_bandana", "hat", "Red Bandana", 250, "bandana", "#c0283a", "#ff9db0"),
  it("hat_catbeanie", "hat", "Cat Beanie", 300, "catbeanie", "#26262c"),
  it("hat_shark", "hat", "Shark Hood", 900, "shark", "#2f7ad0", "#e85a78", "#ff8fb0"),
  it("hat_durag_black", "hat", "Black Durag", 180, "durag", "#24242c"),
  it("hat_durag_silver", "hat", "Silver Durag", 220, "durag", "#b8bcc8"),
  it("hat_fitted_black", "hat", "Black Fitted", 220, "fitted", "#1e1e24", "#c6ff00"),
  it("hat_fitted_navy", "hat", "Navy Fitted", 220, "fitted", "#1f3a6a", "#f4f4f8"),
  it("hat_balaclava", "hat", "Black Balaclava", 260, "balaclava", "#24242a"),
  it("hat_dadcap_cream", "hat", "Cream Dad Cap", 160, "dadcap", "#ece4d2", "#c8ccd6"),
  it("hat_dadcap_black", "hat", "Black Dad Cap", 160, "dadcap", "#26262c", "#c8ccd6"),
  it("hat_trucker_pink", "hat", "Pink Trucker Hat", 220, "trucker", "#f4f4f8", "#f07ab4", "#f07ab4"),
  it("hat_trucker_blue", "hat", "Baby Blue Trucker Hat", 220, "trucker", "#a6d0f2", "#f4f4f8", "#f07ab4"),
  it("hat_bandana_pink", "hat", "Pink Bandana", 220, "bandana", "#f07ab4", "#ffffff"),
  it("hat_bandana_denim", "hat", "Denim Bandana", 220, "bandana", "#5a7aaa", "#e8eef8"),
  it("hat_clips", "hat", "Butterfly Clips", 180, "clips", "#f07ab4", "#86bce8", "#c6ff00"),
  // Glasses
  it("glasses_black", "glasses", "Black Shades", 200, "shades", "#141418", "#6c6c80"),
  it("glasses_tinted", "glasses", "Tinted Glasses", 220, "tinted", "#f0a040"),
  it("glasses_star", "glasses", "Star Glasses", 300, "star", "#ffd23f"),
  it("glasses_y2kvisor", "glasses", "Y2K Visor", 450, "visor", "#7ad8ff", "#ffffff"),
  it("glasses_shield", "glasses", "Slim Shields", 280, "shield", "#2a2a3a", "#ff5fd2"),
  it("glasses_shield_chrome", "glasses", "Chrome Slits", 320, "shield", "#c4c8d4", "#ffffff"),
  it("glasses_ovals", "glasses", "Oval Shades", 240, "ovals", "#141418", "#7a7a8c"),
  it("glasses_ovals_red", "glasses", "Red Oval Shades", 260, "ovals", "#a01828", "#ff8a96"),
  it("glasses_slim_black", "glasses", "Slim Shades", 220, "slim", "#141418", "#6a6a7a"),
  it("glasses_slim_chrome", "glasses", "Slim Chrome Shades", 240, "slim", "#b8bcc8", "#ffffff"),
  it("glasses_aviator_pink", "glasses", "Pink Tinted Aviators", 260, "aviator", "#f07ab4", "#ffffff", "#d8b860"),
  it("glasses_aviator_blue", "glasses", "Blue Tinted Aviators", 260, "aviator", "#6aa6e8", "#ffffff", "#c8ccd6"),
  it("glasses_tinted_pink", "glasses", "Pink Tinted Glasses", 220, "tinted", "#f48cc0"),
  // Accessories
  it("acc_chain", "acc", "Silver Chain", 150, "chain", "#d8d8e0", "#ffd23f"),
  it("acc_headphones", "acc", "Headphones", 300, "headphones", "#c9cbd6", "#7d7f8c"),
  it("acc_headphones_black", "acc", "Black Headphones", 300, "headphones", "#2e2e36", "#5a5a66"),
  it("acc_bunnyphones", "acc", "Bunny Headphones", 500, "bunnyphones", "#f4f4f8", "#ffb3c8", "#ff6f9a"),
  it("acc_sling_black", "acc", "Black Sling Bag", 260, "sling", "#24242a", "#c6ff00"),
  it("acc_sling_silver", "acc", "Silver Sling Bag", 260, "sling", "#b8bcc8", "#1e1e24"),
  it("acc_spike_collar", "acc", "Spike Collar", 180, "collar", "#1e1e24", "#e0e2ea"),
  it("acc_padlock", "acc", "Padlock Chain", 240, "padlock", "#d8d8e0", "#b8bcc8"),
  it("acc_hoops_gold", "acc", "Gold Hoops", 200, "hoops", "#e2b84a", "#fff0a8"),
  it("acc_hoops_silver", "acc", "Silver Hoops", 200, "hoops", "#c8ccd6", "#ffffff"),
  it("acc_chain_gold", "acc", "Gold Chain", 180, "chain", "#e2b84a", "#f4f0e8"),
  it("acc_shoulderbag", "acc", "Shoulder Bag", 300, "baguette", "#1e1e24", "#d8b860"),
  it("acc_shoulderbag_pink", "acc", "Pink Shoulder Bag", 300, "baguette", "#f07ab4", "#f4f4f8"),
  it("acc_choker_rhinestone", "acc", "Rhinestone Choker", 200, "choker", "#c4c8d6", "#ffffff"),
  it("acc_choker_butterfly", "acc", "Butterfly Choker", 220, "choker", "#26262c", "#c4c8d6", "#f07ab4"),
  // Auras
  it("aura_sparkle", "aura", "Sparkles", 800, "sparkle", "#fff27a"),
  it("aura_hearts", "aura", "Hearts", 700, "hearts", "#ff4f7b"),
];

export const ITEM_BY_ID: Record<string, Item> = Object.fromEntries(ITEMS.map((i) => [i.id, i]));

// Swatch colors for the wardrobe; the sprite ramps for skin live in chibi.ts.
export const SKIN_TONES = ["#ffe6d8", "#fddabb", "#efc59d", "#d4a273", "#a97248", "#7c4f32"];
export const HAIR_COLORS = ["#2a2230", "#5a3a2a", "#a0603a", "#e8d9a0", "#d8d4e0", "#f0a6c0", "#5a7ad8", "#9a6ad8", "#c03040"];
/** Hairstyles, by Look.hairStyle index, so new ones are only ever added at the end. Sprites live in chibi.ts. */
export interface Hair { id: string; name: string; price: number }
export const HAIRS: Hair[] = [
  { id: "fluffy", name: "Fluffy", price: 200 },
  { id: "bob", name: "Bob", price: 150 },
  { id: "long", name: "Long", price: 250 },
  { id: "wavy", name: "Wavy", price: 300 },
  { id: "braids", name: "Braids", price: 250 },
  { id: "spiky", name: "Spiky", price: 150 },
  { id: "bald", name: "Bald", price: 0 },
  { id: "curtains", name: "Curtains", price: 250 },
  { id: "wolf", name: "Wolf Cut", price: 350 },
  { id: "buzz", name: "Buzz Cut", price: 100 },
  { id: "twists", name: "Twists", price: 300 },
  { id: "bun", name: "Slick Bun", price: 200 },
  { id: "sleek", name: "Sleek Part", price: 300 },
  { id: "highlights", name: "Chunky Highlights", price: 350 },
  { id: "spacebuns", name: "Space Buns", price: 250 },
];
export const HAIR_STYLES = HAIRS.map((h) => h.id);
export const HAIR_BY_ID: Record<string, Hair> = Object.fromEntries(HAIRS.map((h) => [h.id, h]));
const BALD = HAIR_STYLES.indexOf("bald");
export const EYE_COLORS = ["#5a6070", "#7a4a32", "#3a8ad8", "#e0407a", "#8a5ad8", "#3aa070"];

export interface Look {
  skin: number;
  hair: number;
  hairStyle: number;
  eyes: number;
  top: string;
  bottom: string;
  shoes: string;
  hat: string | null;
  glasses: string | null;
  acc: string | null;
  aura: string | null;
}

export const DEFAULT_LOOK: Look = {
  skin: 1,
  hair: 0,
  hairStyle: BALD,
  eyes: 0,
  top: "top_tee_white",
  bottom: "bottom_shorts_denim",
  shoes: "shoes_white",
  hat: null,
  glasses: null,
  acc: null,
  aura: null,
};

/** What a new player starts in: white tee, denim shorts, white sneakers and no hair. Skin, hair color and eyes are random. */
export function starterLook(rand = Math.random): Look {
  return {
    ...DEFAULT_LOOK,
    skin: Math.floor(rand() * SKIN_TONES.length),
    hair: Math.floor(rand() * HAIR_COLORS.length),
    eyes: Math.floor(rand() * EYE_COLORS.length),
  };
}

/** A random look from the whole catalog, for the crew on the landing page. */
export function randomLook(rand = Math.random): Look {
  const pick = <T,>(a: readonly T[]) => a[Math.floor(rand() * a.length)];
  const any = (slot: Slot) => pick(ITEMS.filter((i) => i.slot === slot)).id;
  return {
    ...starterLook(rand),
    hairStyle: Math.floor(rand() * HAIR_STYLES.length),
    top: any("top"),
    bottom: any("bottom"),
    shoes: any("shoes"),
  };
}

/** Clothes and hairstyles that were free before the starter kit; players from back then keep them. */
export const FORMERLY_FREE_ITEMS = ["top_tee_black", "top_hoodie_grey", "bottom_baggy_blue", "bottom_baggy_black", "shoes_black"];
export const FORMERLY_FREE_HAIRS = ["fluffy", "bob", "long", "wavy", "braids", "spiky"];

export const ownsHair = (id: string, hairs: readonly string[]) => HAIR_BY_ID[id]?.price === 0 || hairs.includes(id);

const clampIdx = (v: unknown, len: number, d: number) =>
  Number.isInteger(v) && (v as number) >= 0 && (v as number) < len ? (v as number) : d;

/** Sanitize a look sent by a client (or saved before a catalog change) against what the player owns. */
export function sanitizeLook(raw: any, owned: Set<string>, hairs: readonly string[]): Look {
  const style = clampIdx(raw?.hairStyle, HAIR_STYLES.length, DEFAULT_LOOK.hairStyle);
  const pick = (slot: Slot, v: unknown): string | null => {
    if (typeof v !== "string") return null;
    const it = ITEM_BY_ID[v];
    if (!it || it.slot !== slot) return null;
    return it.price === 0 || owned.has(v) ? v : null;
  };
  return {
    skin: clampIdx(raw?.skin, SKIN_TONES.length, DEFAULT_LOOK.skin),
    hair: clampIdx(raw?.hair, HAIR_COLORS.length, DEFAULT_LOOK.hair),
    hairStyle: ownsHair(HAIR_STYLES[style], hairs) ? style : DEFAULT_LOOK.hairStyle,
    eyes: clampIdx(raw?.eyes, EYE_COLORS.length, DEFAULT_LOOK.eyes),
    top: pick("top", raw?.top) ?? DEFAULT_LOOK.top,
    bottom: pick("bottom", raw?.bottom) ?? DEFAULT_LOOK.bottom,
    shoes: pick("shoes", raw?.shoes) ?? DEFAULT_LOOK.shoes,
    hat: pick("hat", raw?.hat),
    glasses: pick("glasses", raw?.glasses),
    acc: pick("acc", raw?.acc),
    aura: pick("aura", raw?.aura),
  };
}

/** Prices of cosmetics from the first catalog, refunded when a saved profile still owns them. */
export const RETIRED_ITEMS: Record<string, number> = {
  shirt_gold: 400, shirt_neon: 250, shirt_tux: 600,
  hat_cap: 100, hat_beanie: 120, hat_party: 150, hat_top: 350, hat_crown: 1000,
  glasses_round: 80, glasses_shades: 200, glasses_visor: 450,
};

/** Emotes: wave is free, the rest are bought with Bits. Animations live in src/client/avatar.ts. */
export interface Emote { id: string; name: string; price: number; ms: number }
export const EMOTES: Emote[] = [
  { id: "wave", name: "Wave", price: 0, ms: 2200 },
  { id: "cheer", name: "Cheer", price: 100, ms: 2200 },
  { id: "jump", name: "Jump", price: 150, ms: 2400 },
  { id: "dance", name: "Dance", price: 200, ms: 5000 },
  { id: "spin", name: "Spin", price: 250, ms: 2000 },
];
export const EMOTE_BY_ID: Record<string, Emote> = Object.fromEntries(EMOTES.map((e) => [e.id, e]));

export const STARTING_BITS = 500;
export const DAILY_BITS = 100; // free Bits a player can claim once a day

/**
 * Bits sold for real money through Stripe. Prices are in US cents. The base rate is 100 Bits a dollar
 * (200 for $1.99); bigger packs add bonus Bits on top.
 */
// cents: the price in US cents. mxn: the price in Mexico, in centavos. Stripe can only charge Mexican cards in
// pesos, so players in Mexico pay mxn and everyone else pays cents.
export interface BitsPack { id: string; bits: number; bonus: number; cents: number; mxn: number; tag?: string }
export const BITS_PACKS: BitsPack[] = [
  { id: "bits_200", bits: 200, bonus: 0, cents: 199, mxn: 3900 },
  { id: "bits_525", bits: 525, bonus: 25, cents: 499, mxn: 9900 },
  { id: "bits_1100", bits: 1100, bonus: 100, cents: 999, mxn: 19900, tag: "Popular" },
  { id: "bits_2300", bits: 2300, bonus: 300, cents: 1999, mxn: 39900 },
  { id: "bits_6000", bits: 6000, bonus: 1000, cents: 4999, mxn: 99900 },
  { id: "bits_12500", bits: 12500, bonus: 2500, cents: 9999, mxn: 199900, tag: "Best value" },
];
export type PayCurrency = "usd" | "mxn";
/** A pack's price in the smallest unit of the currency. */
export const packPrice = (p: BitsPack, cur: PayCurrency) => (cur === "mxn" ? p.mxn : p.cents);
/** How a price is shown: $4.99 or MX$99. */
export const showPrice = (p: BitsPack, cur: PayCurrency) =>
  cur === "mxn" ? `MX$${(p.mxn / 100).toLocaleString("en-US")}` : `$${(p.cents / 100).toFixed(2)}`;
export const PACK_BY_ID: Record<string, BitsPack> = Object.fromEntries(BITS_PACKS.map((p) => [p.id, p]));
